import classNames from "classnames";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
} from "date-fns";
import { FC, useState } from "react";

import { ReactComponent as ArrowIcon } from "assets/images/common/arrow.svg";

import styles from "./style.module.scss";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type PropTypes = {
  value: string;
  onSelect: (date: string) => void;
};

const TrendCalendar: FC<PropTypes> = ({ value, onSelect }) => {
  const selected = parseISO(value);
  const today = startOfToday();
  const [view, setView] = useState(startOfMonth(selected));

  const gridStart = startOfWeek(startOfMonth(view), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(view), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const canNext = isBefore(startOfMonth(view), startOfMonth(today));

  return (
    <div className={styles.calendar}>
      <div className={styles.calHead}>
        <button
          type="button"
          className={classNames(styles.calNav, styles.calNavPrev)}
          onClick={() => setView(subMonths(view, 1))}
        >
          <ArrowIcon />
        </button>
        <span className={styles.calTitle}>
          {MONTHS[view.getMonth()]} {view.getFullYear()}
        </span>
        <button
          type="button"
          className={classNames(styles.calNav, !canNext && styles.calNavDisabled)}
          disabled={!canNext}
          onClick={() => setView(addMonths(view, 1))}
        >
          <ArrowIcon />
        </button>
      </div>

      <div className={styles.calWeekdays}>
        {WEEKDAYS.map((weekday) => (
          <span key={weekday}>{weekday}</span>
        ))}
      </div>

      <div className={styles.calGrid}>
        {days.map((day) => {
          const disabled = isAfter(day, today);
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled}
              className={classNames(
                styles.calDay,
                !isSameMonth(day, view) && styles.calDayMuted,
                isSameDay(day, selected) && styles.calDaySelected,
              )}
              onClick={() => onSelect(format(day, "yyyy-MM-dd"))}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TrendCalendar;
